import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Check, Clock, DollarSign, Truck, Plane, Ship } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { quoteAPI } from '@/lib/api';

const CarrierComparison = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { carriers, quoteData, creditsRequired } = location.state || {};
  
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!carriers || !quoteData) {
      toast.error('No carrier data available');
      navigate('/quote/new');
    }
  }, [carriers, quoteData, navigate]);

  const handleSelectCarrier = async (carrier) => {
    setLoading(true);
    try {
      const quoteRequest = {
        ...quoteData,
        selected_carrier_id: carrier.carrier_name
      };

      const response = await quoteAPI.createQuote(quoteRequest);
      toast.success(`Quote created with ${carrier.carrier_name}!`);
      navigate(`/quote/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quote');
    } finally {
      setLoading(false);
    }
  };

  if (!carriers || !quoteData) {
    return null;
  }

  const Icon = quoteData.quote_type === 'CIF' ? Ship : Plane;

  // Sort carriers by price
  const sortedCarriers = [...carriers].sort((a, b) => a.price - b.price);

  return (
    <div className="min-h-screen bg-surface py-8">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate('/quote/new')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Quote Form
        </Button>

        <div className="mb-8">
          <h1 data-testid="comparison-heading" className="text-3xl font-chivo font-bold text-slate-900 mb-2">
            Compare Carriers
          </h1>
          <p className="text-slate-600">
            Select your preferred carrier to finalize your {quoteData.quote_type.toUpperCase()} quote
          </p>
        </div>

        <Card className="p-6 mb-8 bg-slate-50 border-slate-200">
          <div className="flex items-center gap-4 mb-4">
            <Icon className="w-8 h-8 text-accent" />
            <div>
              <h3 className="text-lg font-chivo font-semibold text-slate-900">
                {quoteData.origin_port} → {quoteData.destination_port}
              </h3>
              <p className="text-sm text-slate-600">
                {quoteData.weight_kg} kg | {quoteData.volume_cbm} CBM | {quoteData.cargo_description}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <div>
              <span className="text-slate-600">Credits Required:</span>
              <span className="font-semibold text-accent ml-2">{creditsRequired} credits</span>
            </div>
            <div>
              <span className="text-slate-600">Options Available:</span>
              <span className="font-semibold text-slate-900 ml-2">{carriers.length} carriers</span>
            </div>
          </div>
        </Card>

        {sortedCarriers.length === 0 ? (
          <Card className="p-12 text-center">
            <Truck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-chivo font-semibold text-slate-900 mb-2">
              No carriers available
            </h3>
            <p className="text-slate-600 mb-6">
              Unable to fetch carrier rates at this time. Please try again.
            </p>
            <Button onClick={() => navigate('/quote/new')}>
              Back to Quote Form
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {sortedCarriers.map((carrier, index) => (
              <Card
                key={index}
                data-testid={`carrier-option-${index}`}
                className={`p-6 cursor-pointer transition-all duration-200 hover:shadow-lg ${
                  selectedCarrier === carrier.carrier_name
                    ? 'border-2 border-accent bg-orange-50'
                    : 'border-2 border-transparent hover:border-slate-300'
                }`}
                onClick={() => setSelectedCarrier(carrier.carrier_name)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <Truck className="w-6 h-6 text-slate-900" />
                      <div>
                        <h3 className="text-xl font-chivo font-bold text-slate-900">
                          {carrier.carrier_name}
                        </h3>
                        <p className="text-sm text-slate-600">{carrier.service_type}</p>
                      </div>
                      {index === 0 && (
                        <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-sm">
                          BEST PRICE
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-slate-600" />
                        <div>
                          <p className="text-xs text-slate-600">Price</p>
                          <p className="text-lg font-chivo font-bold text-slate-900">
                            ${carrier.price.toFixed(2)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-slate-600" />
                        <div>
                          <p className="text-xs text-slate-600">Transit Time</p>
                          <p className="text-lg font-chivo font-semibold text-slate-900">
                            {carrier.transit_days} days
                          </p>
                        </div>
                      </div>

                      {carrier.estimated_arrival && (
                        <div>
                          <p className="text-xs text-slate-600">Estimated Arrival</p>
                          <p className="text-sm font-medium text-slate-900">
                            {new Date(carrier.estimated_arrival).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-3 ml-4">
                    {selectedCarrier === carrier.carrier_name && (
                      <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center">
                        <Check className="w-5 h-5 text-white" />
                      </div>
                    )}
                    <Button
                      data-testid={`select-carrier-btn-${index}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectCarrier(carrier);
                      }}
                      disabled={loading}
                      className={selectedCarrier === carrier.carrier_name ? 'bg-accent' : ''}
                    >
                      {loading && selectedCarrier === carrier.carrier_name
                        ? 'Creating Quote...'
                        : 'Select Carrier'}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        <Card className="p-6 mt-8 bg-blue-50 border-blue-200">
          <h4 className="font-chivo font-semibold text-slate-900 mb-2">Important Note</h4>
          <p className="text-sm text-slate-700">
            • Selecting a carrier will deduct <strong>{creditsRequired} credits</strong> from your account
            <br />• Rates are subject to change and will be confirmed with the carrier
            <br />• Transit times are estimates and may vary based on customs and local conditions
            <br />• All prices shown are in USD
          </p>
        </Card>
      </div>
    </div>
  );
};

export default CarrierComparison;
